import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CommentThread } from '../../lib/comment-types';
import { CommentBody } from './CommentBody';
import { filterThreads, type ThreadFilter } from './filters';
import { useLayout } from '../../app/layout';
import { X } from 'lucide-react';
import { Button, cn, IconButton, RadioGroup } from '../../ui';
import { Icon } from '../../ui/icons';

type Props = {
  threads: readonly CommentThread[];
  filter: ThreadFilter;
  onFilter: (f: ThreadFilter) => void;
  onSelect: (thread: CommentThread) => void;
  onClose: () => void;
  me: { id: string };
  names: ReadonlyMap<string, string>;
  /** Threads with an unread mention or reply for me (they count as "For me", P16-3). */
  notified?: ReadonlySet<string>;
  /** The page a thread is on, shown on its row when the board has several pages (P14); null hides it. */
  pageOf?: (thread: CommentThread) => string | null;
};

const FILTERS: ThreadFilter[] = ['all', 'open', 'forMe', 'resolved'];

/**
 * The board's threads, filtered (open, resolved, mine); a row centres the pin and opens the thread. A docked side panel
 * (a landmark, not a transient layer, P16-2): one works on the canvas with it open, so a press outside leaves it open;
 * its close button, Escape inside it and the comments button close it.
 */
export function CommentsPanel({ threads, filter, onFilter, onSelect, onClose, me, names, notified, pageOf }: Props) {
  const { t, i18n } = useTranslation();
  const phone = useLayout() === 'phone';
  // Closed from inside (Escape, its close button), the focus goes back to what opened it, not to the page (P12a).
  const aside = useRef<HTMLElement>(null);
  const [opener] = useState(() => document.activeElement);
  useLayoutEffect(
    () => () => {
      if (aside.current?.contains(document.activeElement) && opener instanceof HTMLElement && opener.isConnected) opener.focus();
    },
    [opener],
  );
  const list = filterThreads(threads, filter, me.id, notified);
  const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' });
  const ago = (iso: string) => {
    const minutes = Math.round((Date.parse(iso) - Date.now()) / 60_000);
    if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
    if (Math.abs(minutes) < 1440) return rtf.format(Math.round(minutes / 60), 'hour');
    return rtf.format(Math.round(minutes / 1440), 'day');
  };
  return (
    <aside
      ref={aside}
      aria-label={t('comments.panelTitle')}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Escape') onClose();
      }}
      data-fullscreen={phone || undefined}
      className={cn(
        'absolute z-20 flex flex-col gap-3 overflow-hidden bg-chrome p-3 shadow-float',
        // Phone: full screen above the bars (spec §13), clear of the notch and the home indicator.
        phone
          ? 'inset-0 z-40 rounded-none pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-[max(0.75rem,env(safe-area-inset-top))]'
          : 'bottom-20 right-3 top-24 w-80 rounded-2xl max-md:inset-x-3 max-md:w-auto',
      )}
    >
      <div className="flex items-center justify-between">
        <h2 className="font-display text-base font-bold">{t('comments.panelTitle')}</h2>
        <IconButton label={t('comments.close')} icon={<Icon icon={X} size={16} />} size="sm" onClick={onClose} />
      </div>
      <RadioGroup
        variant="segmented"
        label={t('comments.filterLabel')}
        value={filter}
        onValueChange={onFilter}
        options={FILTERS.map((f) => ({ value: f, label: t(`comments.filter.${f}`) }))}
      />
      {list.length === 0 ? (
        <p className="font-sans text-sm text-fg-muted">{t('comments.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-1 overflow-auto">
          {list.map((th) => (
            <li key={th.id}>
              <Button variant="ghost" onClick={() => onSelect(th)} className="h-auto w-full flex-col items-stretch gap-1 whitespace-normal p-2 text-left font-normal">
                <span className="flex w-full justify-between gap-2 font-sans text-xs text-fg-muted">
                  <span className="font-medium text-fg">{th.first?.authorName || t('comments.formerGuest')}</span>
                  {pageOf?.(th) && <span className="min-w-0 flex-1 truncate">{pageOf(th)}</span>}
                  <span>{ago(th.updatedAt)}</span>
                </span>
                {th.first && <CommentBody body={th.first.body.slice(0, 200)} names={names} />}
                {th.count > 1 && <span className="font-sans text-xs text-fg-muted">{t('comments.replies', { count: th.count - 1 })}</span>}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
